/**
 * Time Package Top-Up Modal
 */

import React, { useState } from 'react';
import { platformClient, Organization, TimePackagePricing } from '../api/platformClient';
import { X, Package, Check } from 'lucide-react';

interface TopUpModalProps {
  organization: Organization;
  pricing: TimePackagePricing[];
  onClose: () => void;
  onComplete: () => void;
}

export default function TopUpModal({ organization, pricing, onClose, onComplete }: TopUpModalProps) {
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [depositorName, setDepositorName] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPackage) {
      setError('Please select a package');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await platformClient.createTimePackage(organization.id, {
        packageType: selectedPackage,
        paymentMethod: 'bank_transfer',
        depositorName: depositorName || undefined,
        transferDate: transferDate || undefined,
        bankReference: bankReference || undefined,
        notes: notes || undefined,
      });
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create package');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPricing = pricing.find(p => p.packageType === selectedPackage);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            <Package size={20} />
            Top Up Time Package
          </h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.orgInfo}>
            <span style={styles.label}>Organization</span>
            <span style={styles.orgName}>{organization.name}</span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Select Package *</label>
            <div style={styles.packageGrid}>
              {pricing.filter(p => !p.isEnterprise).map((pkg) => (
                <div
                  key={pkg.packageType}
                  style={{
                    ...styles.packageOption,
                    borderColor: selectedPackage === pkg.packageType ? '#6366f1' : '#475569',
                    backgroundColor: selectedPackage === pkg.packageType ? '#6366f120' : 'transparent',
                  }}
                  onClick={() => setSelectedPackage(pkg.packageType)}
                >
                  {selectedPackage === pkg.packageType && (
                    <Check size={16} color="#6366f1" style={styles.checkIcon} />
                  )}
                  <span style={styles.packageHours}>{pkg.hours}h</span>
                  <span style={styles.packagePrice}>₩{pkg.totalPrice.toLocaleString()}</span>
                  <span style={styles.packageUnit}>₩{pkg.pricePerHour.toLocaleString()}/hr</span>
                  {pkg.discountPercent > 0 && (
                    <span style={styles.discountBadge}>{pkg.discountPercent}% OFF</span>
                  )}
                </div>
              ))}
            </div>

            {/* Enterprise option */}
            {pricing.filter(p => p.isEnterprise).map((pkg) => (
              <div
                key={pkg.packageType}
                style={{
                  ...styles.enterpriseOption,
                  borderColor: selectedPackage === pkg.packageType ? '#22c55e' : '#475569',
                  backgroundColor: selectedPackage === pkg.packageType ? '#22c55e20' : 'transparent',
                }}
                onClick={() => setSelectedPackage(pkg.packageType)}
              >
                {selectedPackage === pkg.packageType && (
                  <Check size={16} color="#22c55e" style={styles.checkIcon} />
                )}
                <div>
                  <span style={styles.enterpriseTitle}>Enterprise</span>
                  <span style={styles.enterpriseDesc}>Unlimited (720h/month)</span>
                </div>
                <span style={styles.enterprisePrice}>₩{pkg.totalPrice.toLocaleString()}/mo</span>
              </div>
            ))}
          </div>

          {selectedPricing && (
            <div style={styles.summary}>
              <div style={styles.summaryRow}>
                <span>Package</span>
                <span>{selectedPricing.packageType}</span>
              </div>
              <div style={styles.summaryRow}>
                <span>Hours</span>
                <span>{selectedPricing.hours} hours</span>
              </div>
              <div style={styles.summaryRow}>
                <span>Validity</span>
                <span>{selectedPricing.validityMonths} month(s)</span>
              </div>
              <div style={{ ...styles.summaryRow, ...styles.summaryTotal }}>
                <span>Total</span>
                <span>₩{selectedPricing.totalPrice.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div style={styles.divider} />

          <div style={styles.field}>
            <label style={styles.label}>Depositor Name</label>
            <input
              type="text"
              value={depositorName}
              onChange={(e) => setDepositorName(e.target.value)}
              style={styles.input}
              placeholder="Enter depositor name"
            />
          </div>

          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Transfer Date</label>
              <input
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Bank Reference</label>
              <input
                type="text"
                value={bankReference}
                onChange={(e) => setBankReference(e.target.value)}
                style={styles.input}
                placeholder="Transaction ID"
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
              placeholder="Additional notes..."
            />
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.7 : 1,
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Add Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #334155',
  },
  title: {
    color: '#f8fafc',
    fontSize: '1.125rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: 0,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  form: {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  error: {
    backgroundColor: '#7f1d1d',
    color: '#fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
  },
  orgInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  orgName: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 500,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    color: '#94a3b8',
    fontSize: '0.875rem',
  },
  packageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
  },
  packageOption: {
    border: '2px solid',
    borderRadius: '8px',
    padding: '0.75rem',
    cursor: 'pointer',
    textAlign: 'center',
    position: 'relative',
    transition: 'all 0.2s',
  },
  checkIcon: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
  },
  packageHours: {
    display: 'block',
    color: '#f8fafc',
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  packagePrice: {
    display: 'block',
    color: '#e2e8f0',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginTop: '0.25rem',
  },
  packageUnit: {
    display: 'block',
    color: '#64748b',
    fontSize: '0.75rem',
  },
  discountBadge: {
    display: 'inline-block',
    backgroundColor: '#22c55e',
    color: 'white',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '0.125rem 0.375rem',
    borderRadius: '999px',
    marginTop: '0.375rem',
  },
  enterpriseOption: {
    border: '2px solid',
    borderRadius: '8px',
    padding: '1rem',
    marginTop: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
  enterpriseTitle: {
    display: 'block',
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
  },
  enterpriseDesc: {
    display: 'block',
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  enterprisePrice: {
    color: '#22c55e',
    fontSize: '1rem',
    fontWeight: 600,
  },
  summary: {
    backgroundColor: '#334155',
    borderRadius: '8px',
    padding: '1rem',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#94a3b8',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
  },
  summaryTotal: {
    color: '#f8fafc',
    fontWeight: 600,
    fontSize: '1rem',
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #475569',
    marginBottom: 0,
  },
  divider: {
    height: '1px',
    backgroundColor: '#334155',
    margin: '0.5rem 0',
  },
  row: {
    display: 'flex',
    gap: '1rem',
  },
  input: {
    backgroundColor: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#f8fafc',
    fontSize: '0.875rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#334155',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
